import React, { useState, useEffect, useRef } from 'react';
import { db, auth, storage, analytics, logEvent } from '../firebase';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { processProfilePhoto } from '../utils/imageUtils';
import { LoadingSpinner, InlineSpinner } from './LoadingSpinner';
import { useToast } from './Toast';

function Profile({ onComplete }) {
  const [nickname, setNickname] = useState('');
  const [region, setRegion] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [nicknameChecking, setNicknameChecking] = useState(false);
  const [nicknameAvailable, setNicknameAvailable] = useState(null);
  const [originalNickname, setOriginalNickname] = useState('');
  const fileInputRef = useRef(null);
  const toast = useToast();

  const regions = [
    '강남구', '강동구', '강북구', '강서구', '관악구',
    '광진구', '구로구', '금천구', '노원구', '도봉구',
    '동대문구', '동작구', '마포구', '서대문구', '서초구',
    '성동구', '성북구', '송파구', '양천구', '영등포구',
    '용산구', '은평구', '종로구', '중구', '중랑구'
  ];

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    if (nickname && nickname !== originalNickname) {
      const timer = setTimeout(() => {
        checkNicknameAvailability(nickname);
      }, 500);
      return () => clearTimeout(timer);
    } else if (nickname === originalNickname) {
      setNicknameAvailable(true);
    }
  }, [nickname, originalNickname]);

  const loadProfile = async () => {
    if (!auth.currentUser) {
      setLoading(false);
      return;
    }
    
    try {
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setNickname(data.nickname || '');
        setOriginalNickname(data.nickname || '');
        setRegion(data.region || '');
        
        if (data.photoURL && typeof data.photoURL === 'string') {
          setPhotoURL(data.photoURL);
        } else {
          setPhotoURL('');
        }
        
        if (!data.nickname || !data.region) {
          setIsNewUser(true);
        }
      } else {
        setIsNewUser(true);
      }
    } catch (error) {
      console.error('프로필 로드 실패:', error);
      toast.error('프로필을 불러올 수 없습니다. 새로고침 해주세요.');
    }
    setLoading(false);
  };

  const checkNicknameAvailability = async (nicknameToCheck) => {
    if (!nicknameToCheck || nicknameToCheck.length < 2) {
      setNicknameAvailable(null);
      return;
    }

    setNicknameChecking(true);

    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('nickname', '==', nicknameToCheck));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setNicknameAvailable(true);
      } else {
        const isDuplicate = snapshot.docs.some(doc => doc.id !== auth.currentUser.uid);
        setNicknameAvailable(!isDuplicate);
      }
    } catch (error) {
      console.error('닉네임 체크 실패:', error);
      setNicknameAvailable(null);
    }

    setNicknameChecking(false);
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);

    try {
      const compressedFile = await processProfilePhoto(file);
      const photoRef = ref(storage, `profile_photos/${auth.currentUser.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(photoRef, compressedFile);
      const downloadURL = await getDownloadURL(photoRef);

      // 기존 사진 삭제 (있으면)
      if (photoURL) {
        try {
          const oldPhotoRef = ref(storage, photoURL);
          await deleteObject(oldPhotoRef);
        } catch (error) {
          console.log('이전 사진 삭제 실패 (무시):', error);
        }
      }

      setPhotoURL(downloadURL);

      await setDoc(doc(db, 'users', auth.currentUser.uid), {
        photoURL: downloadURL,
        updatedAt: new Date()
      }, { merge: true });

      toast.success('사진이 업데이트되었습니다');

      logEvent(analytics, 'update_profile_photo');

    } catch (error) {
      console.error('사진 업로드 실패:', error);
      toast.error('사진 업로드 실패: ' + error.message);
    }

    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!nickname || !region) {
      toast.warning('모든 항목을 입력해주세요');
      return;
    }

    if (nickname.length < 2 || nickname.length > 10) {
      toast.warning('닉네임은 2-10자여야 합니다');
      return;
    }

    if (nicknameAvailable === false) {
      toast.error('이미 사용 중인 닉네임입니다');
      return;
    }

    setSaving(true);

    try {
      const userData = {
        nickname,
        region,
        updatedAt: new Date()
      };

      if (isNewUser) {
        userData.createdAt = new Date();
        userData.email = auth.currentUser.email;
      }

      await setDoc(doc(db, 'users', auth.currentUser.uid), userData, { merge: true });

      setOriginalNickname(nickname);
      toast.success('프로필이 저장되었습니다');

      logEvent(analytics, isNewUser ? 'complete_profile_setup' : 'update_profile', {
        region: region
      });

      if (isNewUser && onComplete) {
        setTimeout(() => {
          onComplete();
        }, 1500);
      }

      setIsNewUser(false);

    } catch (error) {
      console.error('저장 실패:', error);
      toast.error('저장 실패: ' + error.message);
    }

    setSaving(false);
  };

  if (loading) {
    return <LoadingSpinner message="프로필 로딩 중..." />;
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>
        {isNewUser ? '프로필 설정' : '프로필 수정'}
      </h2>

      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.photoSection}>
          <div style={styles.photoWrapper}>
            {photoURL ? (
              <img 
                src={photoURL} 
                alt="프로필" 
                style={styles.photo}
                onError={(e) => {
                  e.target.style.display = 'none';
                  const placeholder = document.createElement('div');
                  placeholder.style.cssText =
                    'width:100%;height:100%;border-radius:50%;background-color:#f5f5f5;display:flex;align-items:center;justify-content:center;font-size:40px;border:1px solid #ddd';
                  placeholder.textContent = '👤';
                  e.target.parentElement.appendChild(placeholder);
                }}
              />
            ) : (
              <div style={styles.photoPlaceholder}>👤</div>
            )}
            {uploading && (
              <div style={styles.photoOverlay}>
                <InlineSpinner size="small" color="#fff" />
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            style={styles.fileInput}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            style={styles.photoButton}
            disabled={uploading}
          >
            {uploading ? '업로드 중...' : '사진 변경'}
          </button>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>닉네임 *</label>
          <input
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="2-10자 닉네임"
            maxLength={10}
            style={{
              ...styles.input,
              ...(nicknameAvailable === false ? styles.inputError : {}),
              ...(nicknameAvailable === true && nickname !== originalNickname
                ? styles.inputSuccess
                : {})
            }}
            required
            disabled={saving}
          />
          {nicknameChecking && (
            <div style={styles.checkingText}>확인 중...</div>
          )}
          {nicknameAvailable === false && (
            <div style={styles.errorText}>이미 사용 중인 닉네임입니다</div>
          )}
          {nicknameAvailable === true && nickname !== originalNickname && (
            <div style={styles.successText}>사용 가능한 닉네임입니다</div>
          )}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>지역 (구) *</label>
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            style={styles.select}
            required
            disabled={saving}
          >
            <option value="">선택하세요</option>
            {regions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.infoBox}>
          <div style={styles.infoTitle}>이메일</div>
          <div style={styles.infoValue}>{auth.currentUser?.email}</div>
        </div>

        <button 
          type="submit" 
          style={{
            ...styles.submitButton,
            ...(saving ? styles.submitButtonDisabled : {})
          }}
          disabled={saving || nicknameAvailable === false}
        >
          {saving ? '저장 중...' : (isNewUser ? '시작하기' : '저장하기')}
        </button>

        {!isNewUser && (
          <button 
            type="button"
            onClick={() => {
              if (window.confirm('로그아웃 하시겠어요?')) {
                auth.signOut();
              }
            }}
            style={styles.logoutButton}
          >
            로그아웃
          </button>
        )}
      </form>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '16px'
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '12px',
    borderBottom: '1px solid #ddd',
    paddingBottom: '8px'
  },
  form: {
    paddingTop: '8px'
  },
  photoSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '24px'
  },
  photoWrapper: {
    position: 'relative',
    width: '100px',
    height: '100px',
    marginBottom: '10px'
  },
  photo: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '1px solid #ddd'
  },
  photoPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '40px',
    border: '1px solid #ddd'
  },
  photoOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '50%',
    backgroundColor: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  fileInput: {
    display: 'none'
  },
  photoButton: {
    padding: '8px 16px',
    backgroundColor: '#fff',
    color: '#333',
    border: '1px solid #ccc',
    fontSize: '13px',
    cursor: 'pointer'
  },
  field: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '500',
    color: '#333',
    fontSize: '14px'
  },
  input: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ccc',
    boxSizing: 'border-box',
    outline: 'none'
  },
  inputError: {
    borderColor: '#f44336'
  },
  inputSuccess: {
    borderColor: '#4CAF50'
  },
  select: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ccc',
    boxSizing: 'border-box',
    backgroundColor: 'white'
  },
  checkingText: {
    marginTop: '4px',
    fontSize: '12px',
    color: '#999'
  },
  errorText: {
    marginTop: '4px',
    fontSize: '12px',
    color: '#f44336'
  },
  successText: {
    marginTop: '4px',
    fontSize: '12px',
    color: '#4CAF50'
  },
  infoBox: {
    padding: '10px 0',
    borderTop: '1px solid #eee',
    borderBottom: '1px solid #eee',
    marginBottom: '16px'
  },
  infoTitle: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '4px'
  },
  infoValue: {
    fontSize: '14px',
    color: '#333'
  },
  submitButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#000',
    color: '#fff',
    border: '1px solid #000',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  submitButtonDisabled: {
    backgroundColor: '#999',
    borderColor: '#999',
    cursor: 'not-allowed'
  },
  logoutButton: {
    width: '100%',
    padding: '12px',
    marginTop: '8px',
    backgroundColor: '#fff',
    color: '#333',
    border: '1px solid #ccc',
    fontSize: '14px',
    cursor: 'pointer'
  }
};

export default Profile;
